import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WizardScopeChip from '../components/wizard/WizardScopeChip';
import { useAutoCreateRun } from '../hooks/useAutoCreateRun';
import { GlobalToastStack } from '../components/v2';
import { provision } from '../services/wizardProvisioning';
import { toLaneFeed } from '../services/laneFeed';
import BuildTrail from '../components/wizard/BuildTrail';
import AskInput from '../components/wizard/AskInput';
import ProtectDashboard from '../components/wizard/ProtectDashboard';
import ProtectRail from '../components/wizard/ProtectRail';
import ProtectLive from '../components/wizard/ProtectLive';
import RunCenter from '../components/wizard/RunCenter';
import ProtectChange from '../components/wizard/ProtectChange';
import AgentDetail from '../components/wizard/AgentDetail';
import AccountabilityAsk from '../components/wizard/AccountabilityAsk';
import ResumeKeysWarning from '../components/wizard/ResumeKeysWarning';
import GapDecision from '../components/wizard/GapDecision';
import { useBuildFlow } from '../hooks/useBuildFlow';
import { useWizardDraft } from '../hooks/useWizardDraft';
import useScrollTopOnChange from '../hooks/useScrollTopOnChange';
import { EXTELET_STEPS, scriptFor, clearedTrail, trailFromAnswers } from '../data/buildScripts';
import { archetypeShortLabel } from '../data/agentArchetypes';
import { journeyFor } from '../data/governanceJourney';
import { policySkillIdFrom } from '../data/buildScripts';
/* The same predicate the console's Connect step branches on, rather than a
   second list of which frameworks are coded-SDK. */
import { isCodedFramework } from '../components/v2/frameworkCatalog';
import {
  readConnections, readProvidedAllowance, readBoundaries, applyBoundaries,
  readBindableIntents, readTeam, readIdentity, readJourneyState, readIntentBindings,
  readConnectorSkills, readEvalState, readRunTimeTrust, readPhaseGate,
} from '../services/protectBuild';
import { getAgents as listAgents, getWizardEstate } from '../services/api';
import { runnableAgents } from '../services/runCenter';
import { useAuth } from '../contexts/AuthContext';
import { nominateOwner, readBuildAnswers } from '../services/protectBuild';
import { ESTATE_AGENTS } from '../data/estateAgentCatalogue';
import { MONO_BADGE } from '../components/wizard/typeScale';
import { verdictFromGate } from '../data/phaseGateChecks';

/** Where a half-finished Protect build is kept, locally and on the server. */
const PROTECT_BUILD_DRAFT_KEY = 'prove7:protect-build-draft';

/** The journey stage, as a person reads it. */
const STAGE_WORDS = {
  identity: 'Identity', intent: 'Intent', access: 'Access', promote: 'Promote',
};

/* The step a stage is re-entered at. A stage can hold several steps; coming
   back lands on its first, because the ones after it depend on it. */
const FIRST_STEP_OF = {
  identity: 'identity', intent: 'intent', access: 'access', promote: 'skills',
};

/* Stable ref so the draft service's memoised callbacks do not churn each
   render. `name` gives the central Drafts list a label from the build's own
   answer rather than a generic one. */
const PROTECT_DRAFT_SERVER = {
  archetype: 'extelet',
  name: (snap) => snap?.form?.seed?.answers?.agent?.name || snap?.form?.tile?.name || null,
};

/**
 * Smart Agent Wizard — the console.
 *
 * One column: the chooser, and then the build as a single vertical sequence.
 *
 * <h3>Why the three columns went</h3>
 *
 * It was a left rail of the visitor's four steps, a centre holding one thing,
 * and a right lane of what the platform had proved. The split had a real reason
 * — they are different people's progress — but it put the question in one
 * column and the evidence for it in another, and the first time a build paused
 * the centre said "Working on it" while neither column was telling the truth.
 *
 * Folded into {@link BuildTrail}, the order on screen is the order things
 * happened, and whose step it is travels with the node instead of with the
 * column. The distinction the split was protecting is still drawn; it is just
 * drawn per-step now rather than per-column.
 *
 * <h3>Drawn in p7, not v2</h3>
 *
 * This is the approved palette, from p7-tokens.css. Every class is from
 * p7-components.css and no rule here holds a literal colour, so a rebrand stays
 * one file. The v2 component set remains the default everywhere else — this is
 * not a second button library, it is the one surface whose whole job is
 * reporting what the platform allowed, held or refused, and v2 has no word for
 * the last of those.
 *
 * <h3>What this page owns</h3>
 *
 * Deciding the archetype and calling the backend path that produces it —
 * see services/wizardProvisioning.js. It hands off to nothing: an ExteLet is
 * registered here, an AutoLet is drafted here, and a Let is created here. The
 * archetype is not a field anyone sets; it is derived from the flags that only
 * the creating endpoint writes (Agent.java:795-805), so choosing the endpoint
 * IS choosing the archetype.
 *
 * Lifecycle phases are named in the lane and nowhere else. A visitor should not
 * have to learn Build / Decide / Act to use the product; they can read it as
 * status if they look.
 */
export default function SmartAgentWizardPage() {
  const [params] = useSearchParams();
  /* The page owns every move between screens, including the ones out of it. A
     step that let the browser follow its own link did a document navigation,
     which re-bootstrapped the application and left the walk behind. */
  const navigate = useNavigate();

  // A build already in flight, if any. Null is the ordinary first state.
  //
  // Read from the URL so closing the tab and coming back resumes the same build:
  // useAutoCreateRun replays from its cursor (useAutoCreateRun.js:229-230), so a
  // visitor who wandered off mid-pipeline loses nothing by returning.
  const [runId, setRunId] = useState(() => params.get('runId') || null);

  useEffect(() => {
    const fromUrl = params.get('runId');
    if (fromUrl && fromUrl !== runId) setRunId(fromUrl);
  }, [params, runId]);

  const { session, user } = useAuth();
  /* Read once, as plain values: every call on this page is scoped to them, and a
     dependency array wants an identifier rather than an expression. */
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  // null = not checked yet, true = this session can act here, false = it cannot.
  const [canAct, setCanAct] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  /* Steps the client observed, for the lane.
   *
   * An AutoLet's steps arrive on the pipeline's event stream; an ExteLet has no
   * stream, because `onboard` is one synchronous call that does several distinct
   * things. These are those things, each appended only once it has returned, so the
   * lane stays a record of what happened rather than a prediction of what might.
   * Both sources end up in the same feed and ProveLane cannot tell them apart. */
  const [localSteps, setLocalSteps] = useState([]);
  const flowRef = useRef(null);
  /* What they asked for, shown back at the head of the trail. Held here rather
     than re-derived from the tile, because a typed description has no tile. */
  const [request, setRequest] = useState(null);
  /* A pick waiting on its accountability answer.
     An ExteLet onboard and a native create both establish the ownership record
     in the same call that makes the agent, so there is no later moment at which
     the choice is still cheap — and no moment during the call at which we can
     stop and ask. The question is therefore asked before anything is built. A
     drafted agent is different: it does not exist until review, so that path
     starts immediately and is asked where the agent is real. */
  /**
   * The half-finished build, kept so closing the tab does not lose the walk.
   *
   * <p>The shared wizard draft service — the same one the console's
   * registration wizard uses — writes through to `/api/v1/agent-drafts`, which
   * is scoped to the signed-in person in the tenant they are signed in to:
   * nobody can list, resume or delete anybody else's.</p>
   *
   * <p>Read once, synchronously, before the sequence is built. A draft that
   * arrived a render later would move the question out from under whoever was
   * answering it.</p>
   */
  const buildDraft = useWizardDraft(PROTECT_BUILD_DRAFT_KEY,
    { server: PROTECT_DRAFT_SERVER });
  const [resume] = useState(() => buildDraft.load());
  /* Offered, not imposed. Finding a saved build and dropping somebody straight
     into it takes the front screen away from them — they may have come to do
     something else. The seed is set when they accept the nudge. */
  const [seed, setSeed] = useState(null);
  /* Dropped in this session. `resume` was read once at mount, so clearing the
     stored draft alone would leave it on screen until a reload. */
  const [resumeDropped, setResumeDropped] = useState(false);

  const [pendingPick, setPendingPick] = useState(null);
  /**
   * The gate this agent is standing at, for the verdict band above the whole screen.
   *
   * <h3>Why the surface reads it at all</h3>
   *
   * <p>An agent can be answered question after question while a condition it needs sits unmet
   * three screens away. Measured: a protection policy scored 0.57 by a fallback held an agent
   * in Build through four more questions, and nothing on this surface said so — the gate's own
   * screen had the answer and nobody was on it.</p>
   *
   * <h3>Why it re-reads on the feed</h3>
   *
   * <p>Answers change the gate: measuring the policy is what clears the condition above. The
   * feed growing is the cheapest honest signal that something happened which might have moved
   * it. Null on any failure — {@code verdictFromGate} then draws no band, because "not ready"
   * inferred from a failed read is a claim about the agent made from a fact about the
   * network.</p>
   */
  const [gate, setGate] = useState(null);
  /* Which of the two standing screens is up. The approved surface keeps them
     apart: an account with agents meets its estate, and the catalogue is a
     place you go rather than a thing underneath. Stacking both buries whichever
     one the person came for. */
  /* Which standing screen is up. The front page is the clean one and it is
     always where you land; the estate and the catalogue are places you go. */
  const [view, setView] = useState('front');
  /* The agent the agent-shaped screens are about: the one just built, or the
     one opened from the estate. Without it, Trust, Run and Change have no
     subject and the rail keeps them shut. */
  const [selected, setSelected] = useState(null);

  /* The score the agent has EARNED, read only where it is shown. Once it is
     live and something has run inside the boundary, the summary presents this
     instead of the design-time number it was given before it had done
     anything. Null while unknown or unreadable, so ProtectLive falls back to
     the given score rather than claiming the agent has never run. */
  const [runTimeTrust, setRunTimeTrust] = useState(null);
  useEffect(() => {
    let alive = true;
    setRunTimeTrust(null);
    if (view !== 'live' || !selected?.agentId) return undefined;
    (async () => {
      const t = await readRunTimeTrust(selected.agentId);
      if (alive) setRunTimeTrust(t);
    })();
    return () => { alive = false; };
  }, [view, selected?.agentId]);
  /* What the Promote step's Build phase acts on: the backend it runs on, the
     connectors attached, and how many governed skills it holds. */
  const [connectorSkills, setConnectorSkills] = useState(null);
  /* Where each skill stands against the Build gate's eval bar. */
  const [evalState, setEvalState] = useState(null);

  /* Every screen here swaps inside one route, so the browser resets nothing.
     Keyed on the subject as well as the screen: Trust, Run and Change are the
     same screen for a different agent, and arriving partway down the new one
     is the same defect. */
  useScrollTopOnChange(`${view}:${selected?.agentId || ''}`);

  /**
   * The name tag every agent made here carries.
   *
   * The display name when there is one, the email otherwise — `actorTag` takes the
   * first three letters of the first word either way, so "Sony Abraham" and
   * "sonyabraham@…" both give SON. Not derived from the JWT subject or the user id:
   * a tag has to be recognisable to the person whose agents carry it.
   */
  const actorName = user?.name || user?.email || null;

  /* What this person has already made here.
   *
   * Empty until proven otherwise, and the dashboard is not rendered for an empty list — not as
   * zeroes, not as a dashed invitation. A first visit shows exactly the wizard that ships today,
   * because an empty dashboard reads as a broken screen and the tiles below already say what to
   * do next. A failed read is treated the same way: no dashboard beats a broken one.
   */
  const [estate, setEstate] = useState([]);
  /* Null while the read is fine. Set when it failed, so an empty list can say
     which kind of empty it is. */
  const [estateError, setEstateError] = useState(null);
  /* {agentId: stage} for agents the platform says have an unfinished journey.
     A build abandoned after the commit leaves no draft — the agent is real —
     so where it stopped is read rather than remembered, which is also what
     makes it right when the walk was abandoned in another browser. */
  const [unfinished, setUnfinished] = useState({});
  /* The agent being picked back up, so the keys warning can name it. */
  const [resuming, setResuming] = useState(null);
  /* Set when a build is re-entered on an agent that already exists. Distinct
     from the draft seed: that resumes answers somebody gave and must not land
     past the commit, this resumes platform facts and is allowed to. */
  const [reentry, setReentry] = useState(null);
  /* The agent a nomination is being made for, and what came back. Held
     apart from `selected` because nominating does not open the agent. */
  const [nominating, setNominating] = useState(null);
  const [nominateError, setNominateError] = useState(null);
  const [nominateBusy, setNominateBusy] = useState(false);

  /* Every agent this person has, as the platform served it. Protect reads one
     field off these rows — whether the agent serves governed traffic — and that
     is what decides whether the Run Center has anything to offer. */
  const [agents, setAgents] = useState([]);

  /* What this caller can actually bind an agent to: the frameworks the backend
     supports, the connections Prove7 shares into their tenant, and the ones
     they own — plus whatever allowance their plan carries on ours. Read once
     for the page; the spine asks about it, it does not go and find it. */
  /* What the commit produced. The onboard's envelope carries keys that appear
     on the wire exactly once, so it is held here for the steps that show them
     rather than discarded with the response. */
  const [made, setMade] = useState(null);
  const [boundaries, setBoundaries] = useState([]);
  /* The intents this tenant can actually bind to. Read once the agent exists,
     beside the boundary rules, because both describe what it may now do. */
  const [intents, setIntents] = useState({ intents: [], error: null });
  /* Who this tenant can grant access to. Read beside the intents, once the
     agent exists and there is something to grant on. */
  const [team, setTeam] = useState({ users: [], groups: [], error: null });
  /* The identity the onboard stamped. Read once the agent exists, beside the
     intents and the team, because all three describe an agent that is now real.
     `svidActive: false` on a fresh agent is the normal answer, not a failure:
     the name is minted at registration, a certificate is a separate act. How
     one comes to exist depends on whether the SPIRE Workload API is wired, so
     readIdentity reads that capability rather than asserting a mechanism. */
  const [identity, setIdentity] = useState(null);
  /* What is bound to it already. Read once the agent exists: the Intent step
     must not offer to bind over a live binding, and a refused read is not an
     unbound agent. */
  const [bindings, setBindings] = useState(null);

  const [conns, setConns] = useState({ frameworks: [], error: null });
  const [allowance, setAllowance] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [c, a] = await Promise.all([readConnections(), readProvidedAllowance()]);
      if (!alive) return;
      setConns(c);
      setAllowance(a);
    })();
    return () => { alive = false; };
  }, []);

  /**
   * The estate, and whether we were able to read it.
   *
   * <p>A failure used to land as an empty estate, so a refusal, a dropped
   * request and a genuinely empty estate were one state with one meaning:
   * "you have no agents". That is the claim this surface exists not to make
   * about things it has not established.</p>
   */
  const readEstate = useCallback(async () => {
    try {
      const res = await getWizardEstate();
      const rows = Array.isArray(res) ? res : (res?.data || []);
      setEstate(Array.isArray(rows) ? rows : []);
      setEstateError(null);
    } catch (e) {
      setEstate([]);
      setEstateError(e?.status === 403
        ? 'You do not have permission to list agents in this tenant.'
        : 'Your agents could not be read just now. This is not the same as having none.');
    }
  }, []);

  useEffect(() => { readEstate(); }, [readEstate]);

  /* Which of them are half-finished. Read beside the estate rather than on
     click, so a row can say the stage it stopped at before anybody commits to
     finding out. A failure here leaves the map empty: an agent whose state
     could not be read is not an agent known to be finished, and offering to
     resume one on a guess would land somebody on a stage they had already
     cleared. */
  useEffect(() => {
    let alive = true;
    const ids = estate.map((r) => r.agentId).filter(Boolean);
    if (!ids.length) { setUnfinished({}); return undefined; }
    (async () => {
      /* Per agent, so one read that explodes leaves the other rows alone.
         readJourneyState already turns a refused or failed CALL into an
         error result; this catches the rarer case of it throwing outright. */
      const states = await Promise.all(
        ids.map((id) => readJourneyState(id).catch(() => null)),
      );
      if (!alive) return;
      const next = {};
      ids.forEach((id, i) => {
        const st = states[i];
        if (st && !st.error && st.resumeAt) next[id] = st;
      });
      setUnfinished(next);
    })();
    return () => { alive = false; };
  }, [estate]);

  /** Tile keys this person has already used, so the chooser can mark them. */
  const createdTiles = useMemo(() => {
    const byKey = {};
    estate.forEach((r) => { if (r?.tileKey) byKey[r.tileKey] = r; });
    return byKey;
  }, [estate]);

  /**
   * Everything the steps after the commit need to know about an agent.
   *
   * <h3>Why it is one function</h3>
   *
   * These reads lived inside the commit block, which meant they happened on
   * exactly one of the two ways into those steps. Re-entry set the agent and
   * rebuilt the trail and asked for none of it — so a resumed build ran the
   * post-commit steps blind, and each of them said the honest thing it says
   * when a read comes back empty. The Identity step reported an agent's
   * identity "not readable here" while the platform showed its SPIFFE name on
   * the agent's own page, and the Intent step reported no approved intent in a
   * tenant it had never asked.
   *
   * Both entrances now ask the same question in the same words. A second copy
   * of this list is how one of them goes quietly out of date again.
   *
   * <h3>Why each read is isolated</h3>
   *
   * Fire-and-forget, one at a time. These are not what the caller is for, and
   * one being unavailable must not stop the rest: an undefined read threw
   * before its own .then three separate times, the whole block unwound, and
   * everything after it silently did not happen.
   */
  const loadAgentSideState = useCallback((agentId) => {
    const aside = (run, set) => {
      try {
        Promise.resolve(run()).then(set).catch(() => {});
      } catch { /* a read that is not there is not a build that failed */ }
    };
    aside(() => readBoundaries(), (b) => setBoundaries(b?.categories || []));
    aside(() => readBindableIntents(), setIntents);
    aside(() => readTeam({ orgId, tenantId }), setTeam);
    /* The two that are about this agent rather than about the tenant. */
    if (!agentId) return;
    aside(() => readIdentity(agentId), setIdentity);
    aside(() => readConnectorSkills(agentId), setConnectorSkills);
    aside(() => readEvalState(agentId), setEvalState);
    aside(() => readIntentBindings(agentId), setBindings);
  }, [orgId, tenantId]);

  /**
   * Decide the archetype, then call the one backend path that produces it.
   * An ExteLet lands an agent immediately; an AutoLet lands a runId and the
   * pipeline streams into the lane. Both stay on this surface.
   */
  const start = async (tile, described, accountability, build) => {
    setStartError(null);
    setStarting(true);
    setLocalSteps([]);
    setRequest(described || tile?.prose || tile?.name || null);
    try {
      const out = await provision({
        tile,
        described,
        scope: { orgId, tenantId },
        actorName,
        accountability,
        build,
        /* Two readers, one narration. The lane keeps its own copy for the
           AutoLet pipeline view; the spine records each step as a node, because
           the strip above it claims these stages are cleared and a strip that
           moves while the trail says nothing asks for faith. */
        onStep: (step) => {
          setLocalSteps((prev) => [...prev, step]);
          flowRef.current?.record({
            title: step.title, text: step.text || null, detail: step.detail || null,
            stage: step.stage || null, tone: step.tone || null,
            score: step.score ?? null, band: step.band || null,
          });
        },
      });
      if (out.runId) setRunId(out.runId);
      else if (out.agentId) {
        /* An ExteLet exists the moment it is registered - there is no run to
           stream, so the lane reads its proves directly from the agent the
           selection now points at. A second copy of the id was held here and
           read by nothing. */
        setSelected({ agentId: out.agentId, name: out.name, archetype: out.kind,
          score: out.score });
        /* The spine continues. Registration is the middle of this sequence, not
           the end of it: the keys still have to be saved, the agent still has
           to be wired up and activated, and its boundary rules still have to go
           on. Jumping to the live screen here is what left somebody holding an
           agent they could not use. */
        setMade(out);
        /* What the steps after the commit need. The same call re-entry makes,
           because they are the same steps either way. */
        loadAgentSideState(out.agentId);
      }
      // The tile this came from is now used up, and the dashboard has a row it did not have.
      // Re-read rather than patching local state: the proves and the score are the backend's.
      readEstate();
      /* And ask again whether anything can be run. A registered ExteLet is not
         execution ready and a new native agent is not published, so the honest
         answer is whatever the platform now says — never a local assumption. */
      readAgents();
    } catch (e) {
      setStartError(e?.message || 'Could not start. Try again.');
    } finally {
      setStarting(false);
    }
  };

  /* One read, two answers: whether this session can act here at all, and
     whether any of these agents can be run. A second list call for the same
     rows would be a second answer to the same question. Scoped to the session's
     org and tenant, like every other read on this surface. */
  const readAgents = useCallback(async () => {
    try {
      const res = await listAgents({ orgId, tenantId });
      const rows = Array.isArray(res) ? res : (res?.data || []);
      setAgents(Array.isArray(rows) ? rows : []);
      setCanAct(true);
    } catch (e) {
      // 403 is the one that matters: the session went, or this role cannot act
      // in this scope. Anything else (a network blip) should not lock the page.
      setAgents([]);
      setCanAct(e?.status === 403 ? false : true);
    }
  }, [orgId, tenantId]);

  useEffect(() => { readAgents(); }, [readAgents]);

  const { status, feed, pausedGap, error: runError } = useAutoCreateRun(runId);

  /* The lane's single feed. Ordering, elapsed times and how an event reads all
   * belong to services/laneFeed.js, because the build trail reads the same feed
   * and a second copy is how the two come to disagree. */
  const laneFeed = useMemo(() => toLaneFeed(localSteps, feed), [feed, localSteps]);

  /* Every archetype we have a spine for walks it. One without a spine would be
     walked down somebody else's, which is how a Let ends up being asked which
     framework it runs on. */
  const pick = (a) => {
    if (scriptFor(a.archetype)) setPendingPick(a);
    else start(a, a.prose);
  };

  /* The approved build sequence, walked one node at a time. Every answer is
     collected before anything is called: an ExteLet onboard is a single
     request, so a node claimed earlier would be a prediction. */
  /* The agent the answers belong to. Null until it exists — the steps before the commit are
     answered against a draft, which already persists them — and set from that point on, so each
     answer is kept with the agent rather than only in this tab. */
  /* One resolution of the agent's phase, read from its estate row rather than assumed: the
     row title-cases it and the gate wants it lowercase, so it is lowercased here, once. */
  const pendingAgentId = pendingPick?.agentId || null;
  const pendingPhase = useMemo(() => {
    const raw = (pendingAgentId
      && estate.find((r) => r.agentId === pendingAgentId)?.phase)
      || pendingPick?.phase || null;
    return raw ? String(raw).toLowerCase() : null;
  }, [pendingAgentId, pendingPick, estate]);

  const flow = useBuildFlow(scriptFor(pendingPick?.archetype) || EXTELET_STEPS,
    { seed, resumeFrom: reentry, agentId: made?.agentId || null });
  /* `start` is defined above this line and narrates into the flow, so it reaches
     it through a ref rather than by being moved below it. */
  flowRef.current = flow;

  /* Read once per agent, per phase, and again whenever the record grows. */
  useEffect(() => {
    if (!pendingAgentId || !pendingPhase) { setGate(null); return undefined; }
    let alive = true;
    Promise.resolve(readPhaseGate(pendingAgentId, pendingPhase))
      .then((g) => { if (alive) setGate(g); })
      .catch(() => { if (alive) setGate(null); });
    return () => { alive = false; };
    // flow.log.length is a re-read trigger, not something this effect reads: the record growing
    // is the signal that an answer may have moved the gate. (No disable directive — the
    // project's ESLint config doesn't register react-hooks/exhaustive-deps, and naming an
    // unregistered rule is itself a build-breaking error. Same trap as EvalsAsk.jsx:45.)
  }, [pendingAgentId, pendingPhase, flow.log.length]);

  const verdict = useMemo(() => verdictFromGate(gate, pendingPhase), [gate, pendingPhase]);

  /**
   * Keep the half-finished build, and stop the moment it is no longer half
   * finished.
   *
   * <p>Saved only while nothing has been created. Past the commit there is a
   * real agent with real credentials, and a draft that could reopen the
   * question which registered it would register a second one — which is why
   * the sequence refuses to seed past the commit too. The console draws the
   * same line at its mint.</p>
   */
  useEffect(() => {
    if (!pendingPick || flow.committed || flow.complete) return;
    if (!flow.ask) return;
    buildDraft.save({
      tile: pendingPick,
      seed: { answers: flow.answers, at: flow.ask.key },
    });
  }, [pendingPick, flow.committed, flow.complete, flow.ask, flow.answers, buildDraft]);

  useEffect(() => {
    /* The build is real now: the draft has nothing left to resume, and leaving
       it would nudge somebody back into a walk that already finished. */
    if (flow.committed) buildDraft.clear();
  }, [flow.committed, buildDraft]);

  /* The step the trail is standing on, if it is the person's. One shape for
     both kinds of question, so the trail does not have to know which it is
     rendering — it asks, and whatever answers it goes inside the node. */
  const gapsAwaiting = useMemo(() => Object.keys(pausedGap || {})
    .filter((id) => pausedGap[id]?.reviewKind !== 'confirm_bind'), [pausedGap]);

  const ask = pendingPick
    ? flow.ask
    : (status === 'paused_gap' && gapsAwaiting.length)
      ? {
        number: 1,
        total: gapsAwaiting.length,
        question: gapsAwaiting.length === 1
          ? 'One step we could not match. What should it use?'
          : `${gapsAwaiting.length} steps we could not match.`,
        hint: 'These are the closest things you already have. Nothing is minted and '
            + 'nothing runs until you choose.',
      }
      : null;

  /* The script says what to ask; the page says what the answers can be. Keeping
     the live options out of the script is what lets the script stay a readable
     list of steps rather than a thing that fetches. */
  const liveAsk = (() => {
    if (!ask) return null;
    if (ask.key === 'framework' && conns.frameworks.length) {
      return { ...ask, options: conns.frameworks.map((f) => f.label) };
    }
    if (ask.key === 'words') {
      return { ...ask, fields: ask.fields.map((f) => ({ ...f, value: pendingPick?.prose || '' })) };
    }
    if (ask.key === 'naming') {
      return { ...ask, fields: ask.fields.map((f) => ({
        ...f, value: f.key === 'name' ? pendingPick?.name || '' : '',
      })) };
    }
    if (ask.key === 'agent') {
      /* A coded-SDK agent has no vendor id and no invocation pattern — it runs
         in the customer's own process and calls the gateway itself, so the
         backend sets its id to the minted Agent UUID. The console hides both
         for those frameworks; asking for an id the agent does not have is how
         somebody ends up registering it against the wrong identifier. */
      const platformType = conns.frameworks
        .find((f) => f.label === flow.answers.framework)?.platformType
        || pendingPick?.platformType;
      const coded = isCodedFramework(platformType);
      /* Opened with what the tile already knows, so this is a confirmation
         rather than a transcription exercise — and still editable, because the
         name is theirs and the id may not match what we guessed. The rest keep
         the console's own pre-filled values. */
      const fields = ask.fields
        .filter((f) => !(coded && f.proxiedOnly))
        .map((f) => {
          if (f.key === 'name') return { ...f, value: pendingPick?.name || '' };
          if (f.key === 'externalAgentId') {
            return { ...f, value: pendingPick?.externalAgentId || '' };
          }
          return f;
        });
      return { ...ask,
        fields,
        hint: coded
          ? 'Coded-SDK agent \u2014 no vendor id or invocation pattern. It is protected '
            + 'in-process by the SDK and identified by the Agent UUID minted at '
            + 'registration.'
          : ask.hint };
    }
    if (ask.input === 'credentials') return { ...ask, envelope: made?.envelope || null };
    if (ask.input === 'snippet') {
      return { ...ask, snippets: made?.envelope?.snippets || null,
        path: made?.integrationPath || 'CODED' };
    }
    if (ask.input === 'registered') {
      return { ...ask, envelope: made?.envelope || null, name: made?.name || '',
        path: made?.integrationPath || 'CODED' };
    }
    if (ask.input === 'identity') {
      /* agentId so the step can issue a certificate when the platform's SPIRE
         Workload API is wired — the same mint the identity panel offers. */
      return { ...ask, identity, agentId: made?.agentId || null,
        onOpenIdentity: (path) => navigate(path) };
    }
    if (ask.input === 'intent') {
      return { ...ask, existing: bindings, agentId: made?.agentId || null, intents: intents.intents,
        intentsError: intents.error, scope: { orgId, tenantId } };
    }
    if (ask.input === 'access') {
      return { ...ask, agentId: made?.agentId || null, users: team.users,
        groups: team.groups, teamError: team.error };
    }
    if (ask.input === 'connectorSkills') {
      const id = made?.agentId || null;
      return { ...ask, agentId: id, connectorSkills,
        /* The picker writes connector skills and nothing else.
           This used to ALSO write each one into registration.skillMappings as a
           "capability", which the bridge then materialized as one
           external_policy_proxy apiece. The effect, seen live: the same four
           connector actions appeared three times — under the connector, again
           under Discover skills, and again as PROTECTION POLICY SKILLS — and
           the Build gate counted those proxies ("4 policy skill(s) configured")
           and let the agent through. The tools were being counted as the
           protection.
           It existed only to silence a 400 from materializePolicyConfigurations
           ("Map at least one capability"), which was a reason to ask what a
           capability is, not to invent four. An ExteLet's protection is the one
           authored policy skill; capabilities are what the agent advertises, and
           an operator maps those deliberately on the console's Build surface. */
        onRefreshSkills: async () => {
          setConnectorSkills(await readConnectorSkills(id));
        } };
    }
    if (ask.input === 'policySkill') {
      const id = made?.agentId || null;
      return { ...ask, agentId: id,
        /* Named for the tenant's benefit, so an estate with several can be told
           apart in the Policies list. */
        agentName: made?.name || selected?.name || null };
    }
    if (ask.input === 'evals') {
      const id = made?.agentId || null;
      return { ...ask, agentId: id, evalState,
        onRefreshEvals: () => readEvalState(id).then(setEvalState) };
    }
    /* Both advances go through the GEL lifecycle, which is keyed on the
       registration, and preview the gate, which is keyed on the agent. */
    if (ask.input === 'toDecide' || ask.input === 'toAct') {
      return { ...ask, registrationId: made?.registrationId || null,
        agentId: made?.agentId || null };
    }
    if (ask.input === 'review') {
      return { ...ask, agentId: made?.agentId || null };
    }
    if (ask.input === 'protectionLimits') {
      /* Which policy these limits attach to, as answered two steps ago. Null for an agent with
         one policy — the platform resolves it — and set when the person had to choose between
         several, because attribution between them cannot be guessed. */
      return { ...ask, registrationId: made?.registrationId || null,
        agentId: made?.agentId || null,
        policySkillId: policySkillIdFrom(flow.answers) };
    }
    if (ask.input === 'activate') {
      return { ...ask, registrationId: made?.registrationId || null,
        agentId: made?.agentId || null, archetype: pendingPick?.archetype };
    }
    if (ask.input === 'boundaries') return { ...ask, categories: boundaries };
    if (ask.input === 'connection') {
      const chosen = conns.frameworks.find((f) => f.label === flow.answers.framework)
        || conns.frameworks.find((f) => f.platformType === pendingPick?.platformType)
        || null;
      return { ...ask, framework: chosen, allowance };
    }
    return ask;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh',
                  background: 'var(--p7-canvas)' }}>
    <WizardBar />
    {/* The rail sits above the page on a phone and beside it from lg up; it is the rail's own
        classes that turn, and this is the axis they turn on. */}
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
    <ProtectRail
      view={view}
      estateCount={estate.length}
      selected={Boolean(selected)}
      runnable={runnableAgents(agents).length}
      onGo={(v) => { setPendingPick(null); setView(v); }}
    />
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: '640px',
        background: 'var(--p7-canvas)',
        color: 'var(--p7-ink)',
        fontFamily: 'var(--p7-font-sans)',
      }}
    >
      {/* ── centre: the one thing that needs them ───────────────────────── */}
      {/* One centred column for every screen, so the front page's card, the
          spine beneath it and the estate all sit on the same axis. */}
      <main style={{ minWidth: 0, width: '100%',
                     maxWidth: canAct !== false && (pendingPick || runId) ? '1240px' : '920px',
                     margin: '0 auto', padding: 'var(--p7-space-6)' }}>
        {runError ? (
          <div className="p7-note p7-note--deny" style={{ marginBottom: 'var(--p7-space-4)' }}>
            We lost the connection to this build. It is still running — reopen this page to
            pick it back up.
          </div>
        ) : null}

        {startError ? (
          <div className="p7-note p7-note--deny" style={{ marginBottom: 'var(--p7-space-4)' }}>
            {startError}
          </div>
        ) : null}

        {canAct === false ? (
          <SessionGone />
        ) : pendingPick ? (
          <BuildTrail
            name={pendingPick.name}
            kind={pendingPick.archetype}
            /* Where the journey IS, and the question is the better witness.
               This used to read the last stage the platform narrated, which put
               the journey ahead of the person: the onboard narrates work across
               several stages in one burst, so the pointer jumped to the last of
               them while the next question was still two stages back. Every
               stage in between then rendered as cleared, having never been
               asked. The question is where the person stands; the narration is
               only what has been recorded behind them. */
            stage={ask?.stage
              || [...flow.log].reverse().find((e) => e.stage)?.stage
              || journeyFor(pendingPick.archetype)[0]?.id}
            request={pendingPick.prose || pendingPick.name}
            /* Which phase the agent is actually in — for the Promote stage's own sentence and
               for the verdict band's phase strip. Read from its estate row rather than assumed:
               an agent part-way through has not necessarily reached the phase the walk is asking
               about, and the stage used to assert "promotion complete" over every one of them.
               Absent — a build whose row does not exist yet — is passed through as null, and
               neither the stage nor the band says anything about phases at all. */
            phase={pendingPhase}
            /* Whether it may move on, and what is stopping it. Null until the gate has
               answered; the band is then not drawn rather than drawn cautiously. */
            verdict={verdict}
            feed={flow.log}
            ask={liveAsk}
            onReopen={flow.reopen}
            canReopen={flow.canReopen}
            onExit={() => { setPendingPick(null); setView(estate.length ? 'dashboard' : 'front'); }}
          >
            <AskInput
              ask={liveAsk}
              busy={starting}
              onAnswer={(value) => {
                const answers = { ...flow.answers, [ask.key]: value };
                flow.answer(value);
                /* The script says where the commit is — the last question before
                   anything exists. Everything above it is still a draft; the
                   steps below it report what already happened. */
                if (ask.commit) {
                  const tile = pendingPick;
                  /* A drafted agent hands the trail to the pipeline's own event
                     stream at this point — two producers writing one feed
                     interleave into nonsense — so the spine stands down. */
                  if (tile.archetype === 'autolet') setPendingPick(null);
                  start(tile, tile.prose, answers.accountability, answers);
                  return;
                }
                /* The boundary rules are switched on where they are chosen, so
                   the node under the question records what actually changed. */
                if (ask.key === 'rules' && value?.ruleIds?.length) {
                  applyBoundaries(value.ruleIds).then(({ failed }) => {
                    if (failed.length) {
                      flow.record({ title: `${failed.length} rule`
                        + `${failed.length === 1 ? '' : 's'} would not switch on`,
                      text: 'They are unchanged. Try them from Data Protection.',
                      tone: 'hold' });
                    }
                  });
                }
                if (ask.number === ask.total) {
                  setPendingPick(null);
                  /* Asked for its first call, so land where the call is made —
                     the Run Center, which already exercises an agent with policy
                     live. Anything else would mean a second way to run one. */
                  setView(value === 'Run it now' ? 'run' : 'live');
                }
              }}
            />
          </BuildTrail>
        ) : !runId ? (
          view === 'live' && selected ? (
            <ProtectLive
              name={selected.name}
              ledger={flow.log}
              pending={flow.answers?.accountability?.ownerMode === 'nominate'}
              score={selected.score}
              trust={runTimeTrust}
              onOpen={() => setView('run')}
            />
          ) : view === 'run' ? (
            /* One Run screen, whichever way you arrive. Coming from a build it
               opens on the agent just made — if that agent can actually be run
               yet; if it cannot, the Run Center says so rather than offering a
               call the platform would refuse. */
            <RunCenter
              scope={{ orgId, tenantId }}
              agentId={selected?.agentId || null}
              onRecord={(a) => { setSelected(asSubject(a)); setView('trust'); }}
              onChange={(a) => { setSelected(asSubject(a)); setView('change'); }}
            />
          ) : view === 'trust' && selected ? (
            <AgentDetail
              agentId={selected.agentId}
              name={selected.name}
              archetype={selected.archetype || selected.phase}
              onBack={() => setView(estate.length ? 'dashboard' : 'front')}
            />
          ) : view === 'change' && selected ? (
            <ProtectChange
              agentId={selected.agentId}
              rules={flow.answers?.rules || []}
              intent={flow.answers?.path ? [flow.answers.path] : []}
              owner={flow.answers?.accountability?.ownerMode === 'nominate'
                ? 'Nominated — awaiting acceptance' : actorName}
              onBack={() => setView('run')}
            />
          ) : resuming ? (
            /* Picked back up from the estate. The keys were shown once at the
               mint and cannot be shown again, so this says so before anything
               else happens — and offers a way out rather than only a warning.
               It does not block: whether they were saved is something only the
               person knows, and a tick box asserting it proves nothing. */
            <section className="p7-card" style={{ padding: 'var(--p7-space-5)' }}>
              <h2 className="p7-eyebrow" style={{ marginTop: 0 }}>
                Picking up {resuming.row?.name}
              </h2>
              <p style={{ marginTop: 0, font: 'var(--p7-text-ui)',
                          fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
                {/* The stage is on the state this was opened with, the same
                    read the row named it from. `resuming.stage` is a key
                    nothing ever set, so this said "It stopped at ." */}
                It stopped at {STAGE_WORDS[resuming.state?.resumeAt]
                  || resuming.state?.resumeAt || 'an earlier step'}. Everything
                before that is already on the record.
              </p>
              <ResumeKeysWarning
                agentName={resuming.row?.name}
                onContinue={() => {
                  /* Back into the spine at the stage the platform says is
                     unmet, with the trail above it built from those same
                     reads. `made` is what the post-commit steps hand their
                     agentId to — without it they would have nothing to act
                     on. */
                  const row = resuming.row;
                  const state = resuming.state || { done: {}, resumeAt: null };
                  /* registrationId as well as the agent. The Promote step is
                     called with it — markExecutionReady takes it, and so does
                     the gate checklist — and rebuilding `made` from the estate
                     row alone left it null, so that step rendered an empty
                     checklist under a sentence promising to show what was
                     unmet, above a button that could not be pressed. The read
                     above already fetched it. */
                  setMade({ agentId: row.agentId, name: row.name, kind: row.archetype,
                    registrationId: state.registrationId || null });
                  /* The reads the commit path makes. Without them the steps
                     below report what they see, which is nothing. */
                  loadAgentSideState(row.agentId);
                  /* The trail from what this agent's operator actually answered, where that is
                     on record. Platform facts are the fallback, not the source: they are four
                     lines where there were fourteen answers, which is why Register came back as
                     one line reading "Registered". An agent that predates the record has no rows
                     and gets exactly the old trail. The read is not allowed to hold up the
                     resume — a trail is worth less than the walk it describes. */
                  setReentry({
                    at: FIRST_STEP_OF[state.resumeAt] || state.resumeAt,
                    log: clearedTrail(state.done),
                  });
                  readBuildAnswers(row.agentId).then((stored) => {
                    if (!stored.length) return;
                    setReentry((prev) => (prev ? {
                      ...prev, log: trailFromAnswers(stored, state.done),
                    } : prev));
                  });
                  setPendingPick({
                    name: row.name,
                    archetype: String(row.archetype || 'extelet').toLowerCase(),
                    agentId: row.agentId,
                  });
                  setResuming(null);
                }}
                onStartOver={() => { setResuming(null); setView('catalogue'); }}
              />
              <div style={{ marginTop: 'var(--p7-space-4)' }}>
                <button type="button" className="p7-btn"
                        onClick={() => setResuming(null)}>Back</button>
              </div>
            </section>
          ) : view === 'dashboard' ? (
            <ProtectDashboard
              rows={estate}
              error={estateError}
              scope={{ orgId, tenantId }}
              /* Offered only while it is still resumable — past the commit the
                 draft is cleared, because reopening the question that
                 registered an agent would register a second one. */
              resume={!flow.committed && !resumeDropped && resume?.tile ? resume : null}
              unfinished={Object.fromEntries(
                Object.entries(unfinished).map(([id, st]) => [id, st.resumeAt]))}
              onResumeBuild={(row) => { setResuming({ row, state: unfinished[row.agentId] }); }}
              onResume={(d) => {
                /* Setting the tile is what shows the trail — it takes
                   precedence over the view, so there is no view to set. */
                setSeed(d.seed);
                setPendingPick(d.tile);
              }}
              onDiscardResume={() => { buildDraft.clear(); setResumeDropped(true); }}
              onProtectAnother={() => setView('catalogue')}
              onOpen={(r) => { setSelected(r); setView('trust'); }}
              onNominate={(r) => {
                setNominating(r);
                setNominateError(null);
                setView('nominate');
              }}
            />
          ) : view === 'nominate' && nominating ? (
            /* The same picker the build flow asks with, asked again for an
               agent that already exists. One question, one component, one set
               of words — a second picker here would be the same question
               worded differently depending on when it was asked. */
            <section className="p7-card" style={{ padding: 'var(--p7-space-5)' }}>
              <h2 className="p7-eyebrow" style={{ marginTop: 0 }}>Who answers for this agent?</h2>
              <p style={{ marginTop: 0, font: 'var(--p7-text-ui)',
                          fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
                {nominating.name} has nobody accountable for it. Name the person who does.
              </p>
              <AccountabilityAsk
                busy={nominateBusy}
                onConfirm={async (intent) => {
                  setNominateBusy(true);
                  /* Both spellings, as AuthContext:687 and :794 do — the session
                     carries userId and the user object carries id, and which
                     one is populated depends on how this tab was restored. */
                  const me = user?.id || session?.userId || null;
                  const res = await nominateOwner(nominating.agentId, intent, me);
                  setNominateBusy(false);
                  if (!res.ok) {
                    setNominateError(res.error);
                    return;
                  }
                  /* Read the estate back rather than patching the row: the
                     record's state is the platform's answer, and NOMINATED is
                     not the same claim as ACTIVE. */
                  await readEstate();
                  setNominating(null);
                  setView('dashboard');
                }}
              />
              {nominateError ? (
                <p role="alert" style={{ marginBottom: 0, font: 'var(--p7-text-caption)',
                                         fontFamily: 'var(--p7-font-sans)',
                                         color: 'var(--p7-deny-ink)' }}>
                  {nominateError}
                </p>
              ) : null}
              <button type="button" className="p7-btn p7-btn--sm"
                      style={{ marginTop: 'var(--p7-space-3)' }}
                      onClick={() => { setNominating(null); setView('dashboard'); }}>
                Back to your agents
              </button>
            </section>
          ) : view === 'catalogue' ? (
            <Catalogue
              busy={starting}
              created={createdTiles}
              onBack={() => setView(estate.length ? 'dashboard' : 'front')}
              onDescribe={(text) => start(null, text)}
              onOpen={(r) => { setSelected(r); setView('trust'); }}
              onPick={pick}
            />
          ) : (
            <FrontPage busy={starting} onStart={() => pick(ESTATE_AGENTS[0])}
                       onBrowse={() => setView('catalogue')} />
          )
        ) : (
          /* One sequence, in the order things happened. The two progress
             columns that used to sit either side of this are folded in: whose
             step it is now travels with the node instead of with the column. */
          <BuildTrail request={request} feed={laneFeed} status={status} ask={ask}>
            {ask && status === 'paused_gap'
              ? <GapDecision gaps={pausedGap || {}} runId={runId} />
              : null}
          </BuildTrail>
        )}
      </main>

    </div>
    </div>

    {/* Mounted once by the dashboard shell; this surface has no shell, so without
        this every toast the launcher raises is dropped silently. */}
    <GlobalToastStack />
    </div>
  );
}


/**
 * The wizard's own chrome. It renders outside the dashboard shell, so the way
 * across to the full platform has to live here — a surface with no way out is a
 * trap, however simple it is.
 */
function WizardBar() {
  const navigate = useNavigate();
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--p7-space-4)',
        padding: 'var(--p7-space-3) var(--p7-space-5)',
        background: 'var(--p7-surface)',
        borderBottom: '1px solid var(--p7-line)',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}
      >
        {/* -dark is the DARK artwork, which is what a white surface takes. The
            sidebar uses -light because it is navy. See DashboardLayoutV2:866-868. */}
        <img
          src="/brand/mark-dark.png"
          alt=""
          aria-hidden="true"
          width="26"
          height="22"
          style={{ width: 26, height: 'auto', flex: 'none' }}
        />
        <img
          src="/brand/wordmark-dark.png"
          alt="Prove7"
          width="80"
          height="16"
          style={{ width: 80, height: 'auto', flex: 'none' }}
        />
        <span
          className="p7-eyebrow"
          style={{
            paddingLeft: 'var(--p7-space-2)',
            borderLeft: '1px solid var(--p7-line)',
            whiteSpace: 'nowrap',
          }}
        >
          Protect
        </span>
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center',
                    gap: 'var(--p7-space-3)', minWidth: 0 }}>
        <WizardScopeChip />
        <button
          type="button"
          className="p7-btn p7-btn--sm"
          onClick={() => navigate('/')}
        >
          Switch to the full platform &rarr;
        </button>
      </div>
    </header>
  );
}

/**
 * An agent row, as the agent-shaped screens want it.
 *
 * Trust and Change are written against `agentId`, which is what the estate rows
 * carry. An agent served by /api/v1/agents carries `id`. One translation, in one
 * place, rather than each screen learning both shapes.
 */
const asSubject = (a) => (a ? {
  agentId: a.agentId || a.id, name: a.name, archetype: a.archetype,
} : null);

/**
 * Shown instead of the chooser when the session cannot act here. Says which of
 * the two it is only as far as we can honestly tell them apart from a 403 — the
 * server does not distinguish "signed out" from "not allowed", so neither does
 * this.
 */
function SessionGone() {
  return (
    <>
      <h1 style={H1}>You&rsquo;re signed out</h1>
      <p style={LEDE}>
        Or this account cannot create agents in the workspace you have open. Sign in again
        and you&rsquo;ll come straight back here.
      </p>
      <div style={{ marginTop: 'var(--p7-space-6)' }}>
        <a
          className="p7-btn p7-btn--primary"
          style={{ textDecoration: 'none' }}
          href={`/login?returnTo=${encodeURIComponent('/agents/smartwizard')}`}
        >
          Sign in
        </a>
      </div>
    </>
  );
}

const H1 = {
  margin: 0,
  font: 'var(--p7-text-display)',
  fontFamily: 'var(--p7-font-sans)',
  letterSpacing: '-.02em',
  color: 'var(--p7-ink)',
  textWrap: 'balance',
};

const LEDE = {
  margin: 'var(--p7-space-2) 0 0',
  maxWidth: '64ch',
  font: 'var(--p7-text-body)',
  fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-muted)',
  lineHeight: 1.6,
};

/** The first screen. The proposition, then the agents a customer already runs. */
/**
 * The first screen: describe one, or pick one already in the estate.
 *
 * `created` maps a tile's externalAgentId to the agent this person already made from it. A tile in
 * that map is one-and-done: its action becomes Open rather than Use, and it names the agent it
 * produced. Deleting that agent frees the tile again, which is what makes one-and-done survivable
 * without a second "start over" control.
 *
 * A created tile keeps full contrast and gains a mark. Greying it out would read as unavailable,
 * and it is the opposite of unavailable — it is the one thing on this screen that worked.
 */
/**
 * The front page. The proposition, and one way in.
 *
 * One offer, not the catalogue. A first-time visitor has one thing to do and
 * this is it; the other packs and the two bring-your-own paths are a link away,
 * where somebody who wants to choose will go looking. It is also where you land
 * whether or not you already have agents — those are on the estate, which is a
 * place you go to, not the thing in front of the door.
 */
function FrontPage({ onStart, onBrowse, busy }) {
  const first = ESTATE_AGENTS[0];
  return (
    /* Centred, and the same width as the spine that drops below it when the
       build starts — the card and the sequence share one axis. */
    <div style={{ width: '100%', maxWidth: '760px', margin: '0 auto' }}>
      <h1 style={H1}>Stop worrying, Start Protecting.</h1>
      <p style={{ margin: 'var(--p7-space-3) 0 0', font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)', letterSpacing: '.08em',
                  textTransform: 'uppercase', color: 'var(--p7-ink-subtle)', fontWeight: 500 }}>
        Execution Runtime for Enterprise Agentic Transformation
      </p>
      <p style={{ ...LEDE, marginTop: 'var(--p7-space-3)' }}>
        It gets an identity, a boundary it cannot cross, and a record of everything it touched.
      </p>

      <div className="p7-card"
           style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                    marginTop: 'var(--p7-space-6)', padding: 'var(--p7-space-4)',
                    borderLeft: '3px solid var(--p7-brand, var(--p7-accent))' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                        fontWeight: 600 }}>{first.name}</div>
          <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink-subtle)' }}>
            What your people send it — controlled, and proved.
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span className="p7-status">{archetypeShortLabel(first.archetype)}</span>
            {/* No step count. This said "6 steps" above a script that asks
                thirteen, and computing it from the script would only have made
                a true promise out of one that should not be made at all: the
                number is not what somebody needs before they start, and it
                changes as the journey does. The trail counts as it goes, which
                is the honest place for it — a number here is either stale or
                noise. */}
            <span className="p7-status">{first.effort}</span>
          </div>
        </div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy} onClick={onStart}>
          Start
        </button>
      </div>

      <p style={{ ...LEDE, marginTop: 'var(--p7-space-4)' }}>
        Or{' '}
        <button type="button" onClick={onBrowse}
                style={{ border: 0, background: 'transparent', padding: 0,
                         color: 'var(--p7-accent)', font: 'inherit', cursor: 'pointer' }}>
          describe your own →
        </button>
      </p>
    </div>
  );
}

/**
 * What are we protecting?
 *
 * <h3>Two kinds of thing, kept apart</h3>
 *
 * A pack is an agent somebody already runs, which you bring under control. The
 * other two are agents made here — one your team wrote, one we draft from your
 * words. The approved screen separates them, because a visitor should be able
 * to tell which is which from the shape of the screen rather than by reading
 * seven cards and working it out.
 *
 * <h3>Where "describe your own" went</h3>
 *
 * Not a box floating above the packs. It is the third row, with the other thing
 * you can make here, and it carries its own field — so the front page's
 * "describe your own" still lands somewhere you can type.
 */
function Catalogue({ onPick, onDescribe, onOpen, onBack, busy, created }) {
  const [described, setDescribed] = useState('');
  const packs = ESTATE_AGENTS.filter((a) => a.pack);
  const authored = ESTATE_AGENTS.filter((a) => !a.pack);
  const coded = authored.find((a) => a.archetype === 'let');
  const drafted = authored.find((a) => a.archetype === 'autolet');

  /* No step count on a card either. The build script's length is not a promise
     worth making before anybody starts: it is not what they need to decide, and
     it moves whenever the journey does. The trail counts as it goes. */
  const madeFor = (a) => (a.externalAgentId ? (created || {})[a.externalAgentId] : null);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)' }}>
        <h1 style={{ ...H1, flex: 1 }}>What are we protecting?</h1>
        <button type="button" className="p7-btn p7-btn--sm" onClick={onBack}>Back</button>
      </div>
      <p style={{ ...LEDE, marginTop: 'var(--p7-space-2)' }}>
        Pick the agent your people already use. Everything below is brought under control the
        same way — identity, boundary, record.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'var(--p7-space-3)',
          marginTop: 'var(--p7-space-6)',
        }}
      >
        {packs.map((a) => {
          const made = madeFor(a);
          return (
            <div key={a.key} className="p7-card"
                 style={{ display: 'flex', flexDirection: 'column', gap: '6px',
                          padding: 'var(--p7-space-4)', minWidth: 0 }}>
              <span aria-hidden="true" style={MARK}>{a.mark}</span>
              <span style={TILE_NAME}>{a.name}</span>
              <span style={TILE_BLURB}>{a.blurb}</span>
              {a.leak ? (
                <div className="p7-note p7-note--hold" style={{ marginTop: 'var(--p7-space-2)' }}>
                  {a.leak}
                </div>
              ) : null}
              <Mini items={[archetypeShortLabel(a.archetype), a.effort]} />
              <div style={TILE_ACT}>
                {made ? (
                  <button type="button" className="p7-btn p7-btn--sm"
                          onClick={() => onOpen && onOpen(made)}>Open</button>
                ) : (
                  <button type="button" className="p7-btn p7-btn--primary p7-btn--sm"
                          disabled={busy} onClick={() => onPick(a)}>Protect</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {coded ? (
        <>
          <h2 className="p7-eyebrow" style={{ margin: 'var(--p7-space-8) 0 var(--p7-space-3)' }}>
            Or one your team wrote
          </h2>
          <Offer tile={coded} icon={<Brackets />} made={madeFor(coded)} onOpen={onOpen}>
            <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                    onClick={() => onPick(coded)}>Author it</button>
          </Offer>
        </>
      ) : null}

      {drafted ? (
        <>
          <h2 className="p7-eyebrow" style={{ margin: 'var(--p7-space-6) 0 var(--p7-space-3)' }}>
            Or describe one in your own words
          </h2>
          <Offer tile={drafted} icon={<Quill />} testId="authored">
            {/* Typed or not, this row starts the same drafting path — the words
                are the brief, and without them the tile's own brief is used. */}
            <input
              type="text"
              value={described}
              onChange={(e) => setDescribed(e.target.value)}
              placeholder="The runbook our support team follows every morning…"
              aria-label="Describe the agent you want brought under control"
              style={{
                flex: '1 1 220px', minWidth: 0, padding: '9px 12px',
                border: '1px solid var(--p7-line-strong)',
                borderRadius: 'var(--p7-radius-control)',
                font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                color: 'var(--p7-ink)', background: 'var(--p7-surface)',
              }}
            />
            <button
              type="button"
              className="p7-btn p7-btn--primary"
              disabled={busy}
              onClick={() => (described.trim()
                ? onDescribe(described.trim())
                : onPick(drafted))}
            >
              {busy ? 'Starting\u2026' : 'Draft it'}
            </button>
          </Offer>
        </>
      ) : null}
    </>
  );
}

/**
 * One of the two agents you make here, as a full-width row.
 *
 * The packs are a grid of things you recognise; these are not things you
 * recognise, they are decisions. A row gives each one its own line on the page
 * and keeps it out of a grid where it would read as a sixth product.
 */
function Offer({ tile, icon, made, onOpen, testId, children }) {
  return (
    <div className="p7-card" data-testid={testId}
         style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                  padding: 'var(--p7-space-4)', flexWrap: 'wrap' }}>
      <span aria-hidden="true" style={OFFER_ICON}>{icon}</span>
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <span style={{ ...TILE_NAME, display: 'block' }}>{tile.name}</span>
        <span style={{ ...TILE_BLURB, display: 'block', marginTop: '2px' }}>{tile.blurb}</span>
        <Mini items={[archetypeShortLabel(tile.archetype), tile.stepsLabel, tile.effort]} />
      </div>
      {made && onOpen ? (
        <button type="button" className="p7-btn p7-btn--sm"
                onClick={() => onOpen(made)}>Open</button>
      ) : children}
    </div>
  );
}

/** The three facts under a name: what it is, what it takes, how long. */
function Mini({ items }) {
  const shown = (items || []).filter(Boolean);
  if (!shown.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px',
                  margin: 'var(--p7-space-2) 0 0' }}>
      {shown.map((t) => <span key={t} className="p7-status">{t}</span>)}
    </div>
  );
}

const MARK = {
  width: '34px', height: '34px', borderRadius: '9px', display: 'grid', placeItems: 'center',
  background: 'var(--p7-surface-sunken)', border: '1px solid var(--p7-line)',
  ...MONO_BADGE, color: 'var(--p7-ink-subtle)',
  letterSpacing: '.04em', marginBottom: '2px',
};

const OFFER_ICON = {
  width: '38px', height: '38px', flex: 'none', borderRadius: '10px', display: 'grid',
  placeItems: 'center', background: 'var(--p7-accent-bg)', color: 'var(--p7-accent)',
};

const TILE_NAME = {
  font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)', fontWeight: 600,
  color: 'var(--p7-ink)',
};

const TILE_BLURB = {
  font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.45,
};

const TILE_ACT = {
  marginTop: 'auto', paddingTop: 'var(--p7-space-3)',
  borderTop: '1px solid var(--p7-line-soft)', display: 'flex', justifyContent: 'flex-end',
};

const glyph = (children) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const Brackets = () => glyph(<path d="m8 16-4-4 4-4M16 8l4 4-4 4M14 4l-4 16" />);
const Quill = () => glyph(<><path d="M4 20 14 10" /><path d="M9 15 6 20l5-3" />
  <path d="M14 10a6 6 0 0 0 6-6 10 10 0 0 0-8 4 4 4 0 0 0 2 2Z" /></>);
